/**
 * FunChallengeBattle route
 *
 * Active battle UI for a Fun Challenges session.
 *
 * Visual layout ported from NSCL's BattleScreen
 * (nscl/src/app/components/battle/BattleScreen.tsx):
 *
 *   - Adaptive split at 768 px:
 *       wide ≥ 768 → game panel (flex 1) + ScorePanel (30 % width)
 *       narrow      → ScorePanel stacked above the game panel
 *   - Game panel renders the registered adapter, which now overlays the
 *     coordinate prompt inside the board itself (no more giant label above).
 *   - The right rail is the ScorePanel — opponent header, traffic-light
 *     history rows, central SVG circular timer, your header, End Game button.
 *
 * Lifecycle (unchanged):
 *   - Mounted by the global listener navigating here when
 *     `funChallengesBattleStore.status === 'in_battle'`.
 *   - Server's `fun_session_end` flips status to `'ended'`; the result
 *     SlideModal opens with `<BattleResult />` and we POST the result to
 *     `/fun_challenges/v1/square_trainer_results/`.
 *   - Result modal buttons:
 *       Rematch              → navigate to lobby, re-send same-settings challenge
 *       Challenge Other      → navigate to lobby (fresh pick)
 *
 * Side-effect imports `@/Screens/FunChallenges/adapters` so the SquareTrainer
 * adapter is registered before `getAdapter(...)` is called.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';

import {useNavigation} from '@react-navigation/native';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCLoader from '@/Atoms/CCLoader';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes, vs} from '@/Utils/Dimensions';

import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesBattleSocket from '@/Hooks/FunChallengesSocket/useFunChallengesBattleSocket';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useBattleSoundEffects from '@/Hooks/FunChallengesSounds/useBattleSoundEffects';

import {getAdapter} from '@/Screens/FunChallenges/registry';
import '@/Screens/FunChallenges/adapters';
import {BattleType} from '@/Screens/FunChallenges/types';
import type {SquareTrainerAdapterProps} from '@/Screens/FunChallenges/adapters/SquareTrainerAdapter';

import ScorePanel from './components/ScorePanel';
import BattleResult from './components/BattleResult';
import useSaveSquareTrainerResult, {
  type SaveSquareTrainerResultPayload,
} from '@/Hooks/FunChallengesApi/useSaveSquareTrainerResult';
import useSavePositionTrainerResult, {
  type SavePositionTrainerResultPayload,
} from '@/Hooks/FunChallengesApi/useSavePositionTrainerResult';
import useSavePuzzleDashResult, {
  type SavePuzzleDashResultPayload,
  type PuzzleDashPlayerResultPayload,
} from '@/Hooks/FunChallengesApi/useSavePuzzleDashResult';
import useBattlePuzzles from '@/Hooks/FunChallengesApi/useBattlePuzzles';
import {groupAttemptsByPuzzle} from '@/Screens/FunChallenges/util/attempts';

const MIN_WIDE = 768;

function accuracyOf(correct: number, wrong: number): number {
  const denom = correct + wrong;
  return denom === 0 ? 0 : correct / denom;
}

export default function FunChallengeBattle() {
  const navigation = useNavigation<any>();
  const {width: viewportWidth, height: viewportHeight} = useWindowDimensions();
  const isWide = viewportWidth >= MIN_WIDE;

  const session = useFunChallengesBattleStore(s => s.session);
  const status = useFunChallengesBattleStore(s => s.status);
  const scores = useFunChallengesBattleStore(s => s.scores);
  const myIndex = useFunChallengesBattleStore(s => s.myIndex);
  const myMoves = useFunChallengesBattleStore(s => s.myMoves);
  const opponentMoves = useFunChallengesBattleStore(s => s.opponentMoves);
  const myResponses = useFunChallengesBattleStore(s => s.myResponses);
  const endResult = useFunChallengesBattleStore(s => s.endResult);
  const forfeitInFlight = useFunChallengesBattleStore(s => s.forfeitInFlight);
  const reset = useFunChallengesBattleStore(s => s.reset);

  const {sendMove, forfeit, rematch} = useFunChallengesBattleSocket();

  const [forfeitConfirmOpen, setForfeitConfirmOpen] = useState(false);

  // `loggedInUserstore.userId` is never written by login; `userData.user_id`
  // is the authoritative id. Mirror the same fallback `useFunChallengesSocket`
  // uses so persistence and outcome resolution agree on identity.
  const myUserIdFromStore = useLoggedInUserStore(s => s.userId);
  const myUserIdFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const myUserId = String(myUserIdFromUserData || myUserIdFromStore || '');
  const myDisplayName =
    useLoggedInUserStore(
      s =>
        s.userData?.username ||
        s.userData?.name ||
        s.userData?.first_name ||
        '',
    ) ||
    useLoggedInUserStore(s => s.email) ||
    'You';
  const myAvatarUrl = useLoggedInUserStore(s =>
    typeof s.icon === 'string' && s.icon ? s.icon : undefined,
  );
  const opponentName = session?.opponentName || 'Opponent';

  const adapter = useMemo(
    () => getAdapter(session?.battleType ?? BattleType.SQUARE_TRAINER),
    [session?.battleType],
  );

  // Hydrate puzzle rows for any "puzzle walker" session (PTB + PDB). They
  // share the same adapter shape (PositionTrainerAdapterView) so the
  // puzzles fetch + memo path is identical. The query is gated on
  // `enabled: ids.length > 0`, so Square Trainer never fires it.
  const isPTBSession = session?.battleType === BattleType.POSITION_TRAINER;
  const isPDBSession = session?.battleType === BattleType.PUZZLE_DASH;
  const isPuzzleWalkerSession = isPTBSession || isPDBSession;
  const ptbIds = isPuzzleWalkerSession ? session?.coords ?? [] : [];
  const {data: ptbPuzzlesData} = useBattlePuzzles(ptbIds);
  // Stable empty-object fallback so the adapter's React.memo doesn't see a
  // new `puzzles` reference on every BattleScreen re-render (the 200 ms
  // `secondsLeft` countdown triggers one). Without this, the memo would
  // pass through every tick and the chessboard-package would lose its
  // selected-square / possible-moves highlight (its useEffect-based
  // squareStyles mutation only runs on selectedSquare/possibleMoves state
  // change — every other re-render rebuilds squareStyles empty).
  const ptbPuzzles = useMemo(
    () => ptbPuzzlesData?.puzzles ?? {},
    [ptbPuzzlesData?.puzzles],
  );

  // PDB-only: when the player walks past the end of the server-issued
  // sequence (e.g. 50 puzzles), we recycle ONLY the puzzles they got wrong,
  // in first-seen order. Each player has their own queue derived from
  // `myResponses` (which the server keeps in sync via fun_move_broadcast
  // and rejoin), so the opponent's view of the sequence is independent.
  //
  // Implementation: build a derived sequence = original coords + my failed
  // puzzleIds appended. The adapter walks it with the existing
  // `coords[myIndex % length]` logic — modulo only kicks back in if the
  // recycle queue itself is exhausted (i.e. you keep failing the same
  // puzzles), which is the right wrap behaviour.
  //
  // A puzzle is "failed" if its trailing correctness bit on the response
  // string is 0. We dedupe by first occurrence so a puzzle that flipped to
  // solved on a recycle pass doesn't re-enter the queue.
  const adapterCoords = useMemo(() => {
    if (!isPDBSession || !session) return session?.coords ?? [];
    const baseCoords = session.coords ?? [];
    const failedOrder: string[] = [];
    const failedSeen = new Set<string>();
    const solvedSet = new Set<string>();
    for (const raw of myResponses || []) {
      if (typeof raw !== 'string' || !raw) continue;
      const firstColon = raw.indexOf(':');
      const lastColon = raw.lastIndexOf(':');
      if (firstColon === -1 || lastColon === firstColon) continue;
      const puzzleId = raw.slice(0, firstColon);
      const bit = raw.slice(lastColon + 1);
      const solved = bit === '1' || bit === 'true';
      if (solved) {
        solvedSet.add(puzzleId);
        continue;
      }
      if (!failedSeen.has(puzzleId)) {
        failedSeen.add(puzzleId);
        failedOrder.push(puzzleId);
      }
    }
    const recycle = failedOrder.filter(id => !solvedSet.has(id));
    return recycle.length > 0 ? [...baseCoords, ...recycle] : baseCoords;
  }, [isPDBSession, session, myResponses]);

  const totalSec = session?.settings.timerSec ?? 60;
  const endsAt = session?.endsAt ?? 0;
  // Server pre-rolls 300 squares — enough for 90 s × ~3 clicks/s + headroom.
  // A really fast player can still exhaust the array, in which case we wrap
  // around so the prompt never goes blank (the server doesn't care about
  // coord position — it just records the clicked square and increments
  // counters, so wrapping is a pure client read).
  const coordsLen = session?.coords?.length ?? 0;
  const targetSquare =
    coordsLen > 0 ? session?.coords?.[myIndex % coordsLen] ?? '' : '';

  // ── Live countdown ────────────────────────────────────────────────────
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)),
  );

  useEffect(() => {
    if (!endsAt) return;
    // Freeze the displayed countdown the moment the session ends — a
    // forfeit (or any early end) snaps `status` to 'ended' before the
    // natural endsAt; without this guard the ring kept ticking on the
    // result screen.
    if (status === 'ended') return;
    setSecondsLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    const id = setInterval(() => {
      const next = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setSecondsLeft(next);
      if (next <= 0) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [endsAt, status]);

  // ── Timer-warning sound at 6 s remaining ──────────────────────────────
  const {playTimer} = useBattleSoundEffects();
  const playedTimerRef = useRef(false);
  useEffect(() => {
    playedTimerRef.current = false;
  }, [session?.battleId]);
  useEffect(() => {
    if (status !== 'in_battle') return;
    if (secondsLeft !== 6) return;
    if (playedTimerRef.current) return;
    playedTimerRef.current = true;
    playTimer();
  }, [secondsLeft, status, playTimer]);

  const startedAtRef = useRef<string | null>(null);
  if (session && !startedAtRef.current) {
    startedAtRef.current = new Date().toISOString();
  }

  const battleDisabled =
    status !== 'in_battle' ||
    !session ||
    Date.now() >= endsAt ||
    !targetSquare ||
    secondsLeft <= 0;

  const onMove = useCallback(
    (correct: boolean, square: string) => {
      sendMove(correct, square);
    },
    [sendMove],
  );

  // ── Persistence: fire one POST on `fun_session_end`. Dispatch by
  //    `session.battleType`:
  //      SQUARE_TRAINER   → /square_trainer_results/   (p1/p2 columns)
  //      POSITION_TRAINER → /position_trainer_results/ (p1/p2 columns)
  //      PUZZLE_DASH      → /puzzle_dash_results/      (players[] array,
  //                                                    N-player-ready)
  const {mutate: saveSTResult} = useSaveSquareTrainerResult();
  const {mutate: savePTResult} = useSavePositionTrainerResult();
  const {mutate: savePDResult} = useSavePuzzleDashResult();
  const persistedRef = useRef(false);

  useEffect(() => {
    if (status !== 'ended' || !endResult || !session) return;
    if (persistedRef.current) return;

    const myId = String(myUserId || '');
    const oppId = session.opponentId;
    const player1Id = parseInt(myId, 10);
    const player2Id = parseInt(oppId, 10);
    if (!Number.isFinite(player1Id) || !Number.isFinite(player2Id)) {
      return;
    }

    const myFinal = endResult.scores[myId] || {correct: 0, wrong: 0};
    const oppFinal = endResult.scores[oppId] || {correct: 0, wrong: 0};
    const myResp = endResult.responses[myId] || myResponses;
    const oppResp = endResult.responses[oppId] || [];

    let result: 'p1' | 'p2' | 'draw' = 'draw';
    if (endResult.winnerId) {
      result = String(endResult.winnerId) === myId ? 'p1' : 'p2';
    }

    if (session.battleType === BattleType.PUZZLE_DASH) {
      // Reconstruct per-puzzle attempts (same wire overload as PTB).
      const myGrouped = groupAttemptsByPuzzle(myResp);
      const oppGrouped = groupAttemptsByPuzzle(oppResp);

      const puzzleIds = (session.coords || [])
        .map(c => parseInt(c, 10))
        .filter(Number.isFinite);

      const targetCorrect = parseInt(
        String(session.settings.targetCorrect ?? 15),
        10,
      ) || 15;
      const maxWrong = parseInt(
        String(session.settings.maxWrong ?? 5),
        10,
      ) || 5;
      const timerSec = parseInt(
        String(session.settings.timerSec ?? 600),
        10,
      ) || 600;

      const reason = endResult.reason as
        | 'target_reached'
        | 'mistake_out'
        | 'forfeit'
        | 'time_up';
      const finishedAt = new Date().toISOString();

      // v1 ships 2-player only. Build the N-player-ready players[] array
      // from this client's vantage point — defensive: if the server's
      // broadcast omitted a userId from `responses`, log + emit an empty
      // attempts/solved/failed set so the score row still records.
      const buildPlayerRow = (
        uid: string,
        final: {correct: number; wrong: number},
        grouped: ReturnType<typeof groupAttemptsByPuzzle>,
        responsesForUid: string[],
      ): PuzzleDashPlayerResultPayload => {
        if (!endResult.responses[uid] && responsesForUid.length === 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[useSavePuzzleDashResult] no responses for userId',
            uid,
            '— player_results row will have empty attempts/solved/failed',
          );
        }
        return {
          user_id: parseInt(uid, 10),
          score: final.correct,
          wrong: final.wrong,
          accuracy: accuracyOf(final.correct, final.wrong),
          solved_ids: grouped.solvedIds,
          failed_ids: grouped.failedIds,
          attempts: grouped.attempts,
          eliminated: final.wrong >= maxWrong,
          finished_at: finishedAt,
        };
      };

      const players: PuzzleDashPlayerResultPayload[] = [
        buildPlayerRow(myId, myFinal, myGrouped, myResp),
        buildPlayerRow(oppId, oppFinal, oppGrouped, oppResp),
      ];

      const pdPayload: SavePuzzleDashResultPayload = {
        session_id: endResult.battleId,
        game_type: 'puzzle_dash',
        player1_id: player1Id,
        player2_id: player2Id,
        result,
        reason,
        puzzle_ids: puzzleIds,
        players,
        target_correct: targetCorrect,
        max_wrong: maxWrong,
        end_reason: reason,
        time_control_sec: timerSec,
      };

      persistedRef.current = true;
      savePDResult(pdPayload);
      return;
    }

    if (session.battleType === BattleType.POSITION_TRAINER) {
      // Reconstruct the per-puzzle attempt trail from each player's flat
      // response array — every entry is "<puzzleId>:<uci>:<0|1>".
      const myGrouped = groupAttemptsByPuzzle(myResp);
      const oppGrouped = groupAttemptsByPuzzle(oppResp);

      // Coords are strings; the persistence table stores ints.
      const puzzleIds = (session.coords || [])
        .map(c => parseInt(c, 10))
        .filter(Number.isFinite);

      const maxWrong = parseInt(
        String(session.settings.maxWrong ?? 5),
        10,
      ) || 5;

      // `endResult.reason` widened to include 'target_reached' (PDB) — but
      // a POSITION_TRAINER session can never end with that reason, so we
      // narrow defensively.
      const ptReason =
        endResult.reason === 'target_reached'
          ? 'time_up'
          : (endResult.reason as 'time_up' | 'forfeit' | 'mistake_out');

      const ptPayload: SavePositionTrainerResultPayload = {
        session_id: endResult.battleId,
        game_type: 'position_trainer',
        player1_id: player1Id,
        player2_id: player2Id,
        result,
        reason: ptReason,
        puzzle_ids: puzzleIds,
        p1_attempts: myGrouped.attempts,
        p2_attempts: oppGrouped.attempts,
        p1_solved_ids: myGrouped.solvedIds,
        p1_failed_ids: myGrouped.failedIds,
        p2_solved_ids: oppGrouped.solvedIds,
        p2_failed_ids: oppGrouped.failedIds,
        p1_score: myFinal.correct,
        p1_wrong: myFinal.wrong,
        p1_accuracy: accuracyOf(myFinal.correct, myFinal.wrong),
        p2_score: oppFinal.correct,
        p2_wrong: oppFinal.wrong,
        p2_accuracy: accuracyOf(oppFinal.correct, oppFinal.wrong),
        time_control_sec: session.settings.timerSec,
        max_wrong: maxWrong,
        end_reason: ptReason,
      };

      persistedRef.current = true;
      savePTResult(ptPayload);
      return;
    }

    // Square Trainer (default) — wire unchanged from v1.
    const stPayload: SaveSquareTrainerResultPayload = {
      session_id: endResult.battleId,
      game_type: 'square_trainer',
      player1_id: player1Id,
      player2_id: player2Id,
      result,
      // ST result table's `reason` column is varchar(16) but the column
      // value set only accepts 'time_up' | 'forfeit'. PTB introduces
      // 'mistake_out' — map it through as 'forfeit' (semantically closest)
      // so a malformed enum never reaches Django's update_or_create.
      // ST table accepts only 'time_up' | 'forfeit'. Map the PTB/PDB
      // reasons to their closest legal value.
      reason:
        endResult.reason === 'mistake_out' ||
        endResult.reason === 'target_reached'
          ? 'forfeit'
          : (endResult.reason as 'time_up' | 'forfeit'),
      squares: session.coords,
      p1_responses: myResp,
      p2_responses: oppResp,
      p1_score: myFinal.correct,
      p1_wrong: myFinal.wrong,
      p1_accuracy: accuracyOf(myFinal.correct, myFinal.wrong),
      p2_score: oppFinal.correct,
      p2_wrong: oppFinal.wrong,
      p2_accuracy: accuracyOf(oppFinal.correct, oppFinal.wrong),
      time_control_sec: session.settings.timerSec,
      orientation:
        (session.settings.orientation as 'white' | 'black' | 'random') ||
        'white',
      p1_started_at: startedAtRef.current,
      p2_started_at: null,
      p1_finished_at: new Date().toISOString(),
      p2_finished_at: null,
    };

    persistedRef.current = true;
    saveSTResult(stPayload);
  }, [
    status,
    endResult,
    session,
    myUserId,
    myResponses,
    saveSTResult,
    savePTResult,
    savePDResult,
  ]);

  // ── Idle guard ──────────────────────────────────────────────────────
  // If the user lands here without an active session — e.g. by deep-linking
  // straight to /FunChallengeBattle — wait a grace window before bouncing
  // them to the lobby. On a hard refresh the socket needs ~1 s to reconnect
  // and then our `fun_session_rejoin` probe needs another roundtrip to
  // hydrate the session. Bouncing immediately defeated the whole rejoin
  // flow. If after 3.5 s we still don't have a session, then it's safe to
  // assume there's nothing to rejoin and go home.
  useEffect(() => {
    if (status !== 'idle' || endResult) return;
    const id = setTimeout(() => {
      // Re-check at fire time — the rejoin handler may have flipped status
      // to `in_battle` while we were waiting.
      const live = useFunChallengesBattleStore.getState();
      if (live.status === 'idle' && !live.endResult) {
        navigation.navigate('FunChallengesLobby');
      }
    }, 3500);
    return () => clearTimeout(id);
  }, [status, endResult, navigation]);

  const handleBackToLobby = () => {
    reset();
    navigation.navigate('FunChallengesLobby');
  };

  const handleRematch = () => {
    // Navigate to the lobby first so the global listener's "Waiting for
    // opponent…" SlideModal renders against the lobby surface — staying on
    // the spent BattleScreen is jarring while we wait for the new session.
    navigation.navigate('FunChallengesLobby');
    rematch();
  };

  const handleChallengeOther = () => {
    reset();
    navigation.navigate('FunChallengesLobby');
  };

  const handleForfeitTap = () => {
    if (status !== 'in_battle' || forfeitInFlight) return;
    setForfeitConfirmOpen(true);
  };

  const handleConfirmForfeit = () => {
    setForfeitConfirmOpen(false);
    forfeit();
  };

  if (!session) {
    // While a new challenge is being negotiated (outgoing rematch from this
    // screen, or — rarely — an incoming invite that arrived without a prior
    // session to preserve), the global FunChallengesGlobalListener is
    // showing the waiting / accept-decline modal on top. Rendering the
    // "Reconnecting…" fallback here paints it directly behind that modal.
    // Render nothing in those states — the listener owns the UI.
    if (status === 'challenging' || status === 'incoming') {
      return null;
    }
    // The grace-window effect above gives the rejoin probe a chance to
    // hydrate the store. Render the shared CCLoader (lottie chessboard)
    // so a refresh-mid-battle reads as "we're still working" instead of
    // a bare text fallback.
    return (
      <Box style={styles.empty}>
        <CCLoader loading isComponentLoader />
        <CCText style={styles.reconnectText}>Reconnecting…</CCText>
        <CCText style={styles.reconnectHint}>
          Restoring your battle.
        </CCText>
      </Box>
    );
  }

  // ── Board sizing ─────────────────────────────────────────────────────
  // We want a square board that fits inside the available game-panel area.
  // Wide layout: ~70% of viewport minus padding/panel/gap.
  // Narrow: full width up to a sensible cap.
  // Board sizing: take the largest square that fits the available width-budget
  // AND the available height-budget. Previously we capped at 540 px on wide
  // layouts even when the viewport had plenty of vertical room; on most
  // desktops that left a big empty band under the board.
  const SCROLL_PAD = 24;
  const PANEL_GAP = 24;
  // Vertical chrome above the game panel (heading + header pill + outer
  // scroll padding). Conservative; if a viewport is unusually short the
  // floor `Math.max(260, …)` clamp keeps the board legible.
  const VERTICAL_CHROME = 140;
  const panelWidthEstimate = Math.max(280, viewportWidth * 0.3);
  const widthBudget = isWide
    ? viewportWidth - SCROLL_PAD * 2 - PANEL_GAP - panelWidthEstimate
    : viewportWidth - SCROLL_PAD * 2;
  // Narrow layouts stack the ScorePanel above the board; the ring is ~280 px
  // and the player rows ~140 px combined, plus padding.
  const heightBudget = isWide
    ? viewportHeight - VERTICAL_CHROME
    : viewportHeight - VERTICAL_CHROME - 380;
  const wideCap = isWide ? 720 : 520;
  const boardWidth = Math.max(
    260,
    Math.min(wideCap, widthBudget, heightBudget),
  );

  // ── Win/loss/draw resolution ─────────────────────────────────────────
  const myIdForOutcome = session?.myUserId || String(myUserId || '');
  const myFinal = endResult?.scores[myIdForOutcome] || {
    correct: scores.myCorrect,
    wrong: scores.myWrong,
  };
  const oppFinal = endResult?.scores[session.opponentId] || {
    correct: scores.opponentCorrect,
    wrong: scores.opponentWrong,
  };
  const outcome: 'win' | 'loss' | 'draw' = endResult
    ? endResult.winnerId
      ? String(endResult.winnerId) === myIdForOutcome
        ? 'win'
        : 'loss'
      : 'draw'
    : 'draw';

  // Both PTB and PDB have a mistake-out limit + per-player wrong chips.
  // Square Trainer has neither.
  const showWrongChips = isPuzzleWalkerSession;
  const sessionMaxWrong = showWrongChips
    ? typeof session.settings.maxWrong === 'number'
      ? (session.settings.maxWrong as number)
      : 5
    : undefined;
  // PDB only — drives the centre "Race to N" tile + per-player "✓ N of M"
  // chips. Undefined for ST/PTB so the SVG timer ring renders as today.
  const sessionTargetCorrect =
    isPDBSession && typeof session.settings.targetCorrect === 'number'
      ? (session.settings.targetCorrect as number)
      : undefined;

  const scorePanel = (
    <ScorePanel
      myCorrect={scores.myCorrect}
      opponentCorrect={scores.opponentCorrect}
      myWrong={showWrongChips ? scores.myWrong : undefined}
      opponentWrong={showWrongChips ? scores.opponentWrong : undefined}
      maxWrong={sessionMaxWrong}
      targetCorrect={sessionTargetCorrect}
      myMoves={myMoves}
      opponentMoves={opponentMoves}
      totalSec={totalSec}
      secondsLeft={secondsLeft}
      myName={myDisplayName}
      opponentName={opponentName}
      myAvatarUrl={myAvatarUrl}
      onPressEndGame={handleForfeitTap}
      endGameDisabled={status !== 'in_battle' || forfeitInFlight}
      endGameLabel={forfeitInFlight ? 'Forfeiting…' : 'End Game'}
    />
  );

  const gamePanel = (
    <View style={styles.gamePanel}>
      {adapter ? (
        isPuzzleWalkerSession ? (
          adapter.renderGame({
            // PDB swaps in `adapterCoords` so the walker continues onto
            // the player's failed-puzzle recycle queue once the original
            // server sequence is exhausted. PTB is `session.coords`
            // unchanged.
            coords: isPDBSession ? adapterCoords : session.coords,
            myIndex,
            boardWidth,
            puzzles: ptbPuzzles,
            onMove,
            disabled: battleDisabled,
            // PDB hides the "Puzzle N of M" subtitle (no fixed puzzle count
            // we want to surface — sequence is padded + recycled). PTB
            // keeps showing it.
            hideCounter: isPDBSession,
          })
        ) : (
          adapter.renderGame({
            coords: session.coords,
            myIndex,
            orientation: session.settings.orientation as
              | 'white'
              | 'black'
              | 'random',
            boardWidth,
            onMove,
            disabled: battleDisabled,
          } as SquareTrainerAdapterProps)
        )
      ) : (
        <Box style={styles.empty}>
          <CCText>
            Adapter for {session.battleType} not registered.
          </CCText>
        </Box>
      )}
    </View>
  );

  return (
    <View style={[styles.root, styles.rootContent]}>
      <Box
        style={[
          styles.layout,
          isWide ? styles.layoutWide : styles.layoutNarrow,
        ]}>
        {isWide ? (
          <>
            {gamePanel}
            <View style={styles.scorePanelWrapWide}>{scorePanel}</View>
          </>
        ) : (
          <>
            <View style={styles.scorePanelWrapNarrow}>{scorePanel}</View>
            {gamePanel}
          </>
        )}
      </Box>

      <SlideModal
        // Keep the result modal visible if an invite arrives while we're
        // still on the result screen — `setIncomingChallenge` preserves
        // `endResult` in that case so the user doesn't lose their scores
        // behind the accept/deny modal that the global listener stacks
        // above this one.
        visible={
          (status === 'ended' || status === 'incoming') && !!endResult
        }
        onClose={handleBackToLobby}
        modalHeaderTitle="Game over"
        type="center"
        contentPadding={24}>
        {endResult && (
          <BattleResult
            outcome={outcome}
            myCorrect={myFinal.correct}
            myWrong={myFinal.wrong}
            opponentCorrect={oppFinal.correct}
            opponentWrong={oppFinal.wrong}
            myName={myDisplayName}
            opponentName={opponentName}
            myAvatarUrl={myAvatarUrl}
            reason={endResult.reason}
            maxWrong={
              typeof session.settings.maxWrong === 'number'
                ? (session.settings.maxWrong as number)
                : undefined
            }
            targetCorrect={
              typeof session.settings.targetCorrect === 'number'
                ? (session.settings.targetCorrect as number)
                : undefined
            }
            onRematch={handleRematch}
            onChallengeOther={handleChallengeOther}
          />
        )}
      </SlideModal>

      <SlideModal
        visible={forfeitConfirmOpen}
        onClose={() => setForfeitConfirmOpen(false)}
        modalHeaderTitle="End Game?"
        type="center"
        contentPadding={20}>
        <Box>
          <CCText style={styles.confirmText}>
            Your opponent will win. This cannot be undone.
          </CCText>
          <View style={styles.confirmButtons}>
            <Pressable
              onPress={() => setForfeitConfirmOpen(false)}
              style={[styles.confirmBtn, styles.confirmBtnSecondary]}>
              <CCText style={styles.confirmBtnTextSecondary}>
                Cancel
              </CCText>
            </Pressable>
            <Pressable
              onPress={handleConfirmForfeit}
              style={[styles.confirmBtn, styles.confirmBtnDanger]}>
              <CCText style={styles.confirmBtnTextDanger}>
                Yes, Forfeit
              </CCText>
            </Pressable>
          </View>
        </Box>
      </SlideModal>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1, backgroundColor: '#FAF6EB'},
  rootContent: {padding: 24},
  layout: {
    flex: 1,
    gap: 24,
    alignItems: 'stretch',
  },
  layoutWide: {flexDirection: 'row'},
  layoutNarrow: {flexDirection: 'column', gap: 16},
  gamePanel: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  scorePanelWrapWide: {
    width: '30%',
    minWidth: 280,
    alignSelf: 'stretch',
  },
  scorePanelWrapNarrow: {
    width: '100%',
  },
  empty: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 320,
  },
  reconnectText: {
    fontSize: FontSizes[20] || 20,
    fontWeight: '700',
    color: '#262322',
    marginBottom: 6,
  },
  reconnectHint: {
    fontSize: FontSizes[14] || 14,
    color: '#4D3F37',
  },
  confirmText: {
    fontSize: FontSizes[14] || 14,
    color: CCColors.GreyDarkest ?? '#444',
    marginBottom: vs(16),
    textAlign: 'center',
  },
  confirmButtons: {flexDirection: 'row', gap: 12},
  confirmBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmBtnSecondary: {
    borderWidth: 1.5,
    borderColor: '#D9D9D9',
    backgroundColor: CCColors.White,
  },
  confirmBtnDanger: {
    backgroundColor: '#EB5757',
  },
  confirmBtnTextSecondary: {
    color: CCColors.GreyDarkest ?? '#444',
    fontWeight: '600',
  },
  confirmBtnTextDanger: {
    color: CCColors.White,
    fontWeight: '700',
  },
});
